import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import {
  Merchant,
  SoftDeleteFlag,
  Store,
  ShopAndSmileAPIAdapter,
} from '@bofrak-backend/shared';
import { apiAdapter } from '../api/backend';
import {
  adminAtom,
  merchantAtom,
  selectedStoreAtom,
  liteAuthTokenAtom,
  accessTokenAtom,
} from '../recoil/atoms';

const LOG = '[useLiteAuth]';
const SESSION_KEY = 'pos_lite_session';

interface LiteSession {
  token: string;
  merchantId: string;
  storeId: string;
  employee: Record<string, unknown>;
  merchant: Record<string, unknown>;
  store: Record<string, unknown>;
}

interface UseLiteAuthResult {
  isValid: boolean | null;
  errorMessage: string | null;
}

/** Extract a query param without URLSearchParams' + → space corruption */
function getRawQueryParam(name: string): string | null {
  const raw = window.location.search.substring(1);
  if (!raw) return null;
  const pairs = raw.split('&');
  for (const pair of pairs) {
    const idx = pair.indexOf('=');
    if (idx === -1) continue;
    const key = decodeURIComponent(pair.substring(0, idx));
    if (key === name) {
      return decodeURIComponent(pair.substring(idx + 1));
    }
  }
  return null;
}

function loadSession(): LiteSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    console.log(LOG, '📖 loadSession() raw exists:', !!raw);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LiteSession;
    console.log(
      LOG,
      '📖 loadSession() parsed — merchantId:',
      parsed.merchantId,
      'storeId:',
      parsed.storeId,
    );
    return parsed;
  } catch (e) {
    console.error(LOG, '❌ loadSession() parse error:', e);
    return null;
  }
}

function saveSession(session: LiteSession): void {
  console.log(
    LOG,
    '💾 saveSession() — merchantId:',
    session.merchantId,
    'storeId:',
    session.storeId,
  );
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession(): void {
  console.log(LOG, '🗑️ clearSession()');
  localStorage.removeItem(SESSION_KEY);
}

/** Every write this browser sends records the signed-in employee. */
const setActorFrom = (employee: unknown) => {
  const { id, name } = (employee ?? {}) as { id?: string; name?: string };
  ShopAndSmileAPIAdapter.setDefaultActor(
    id ? { id, name: name || id } : undefined,
  );
};

export const useLiteAuth = (): UseLiteAuthResult => {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setMerchant = useSetRecoilState(merchantAtom);
  const setStore = useSetRecoilState(selectedStoreAtom);
  const setAdmin = useSetRecoilState(adminAtom);
  const setStoredToken = useSetRecoilState(liteAuthTokenAtom);
  const setAccessToken = useSetRecoilState(accessTokenAtom);

  console.log(
    LOG,
    '🏁 Hook mounted — isValid:',
    isValid,
    'errorMessage:',
    errorMessage,
  );

  useEffect(() => {
    let cancelled = false;
    console.log(LOG, '🚀 useEffect started');

    const restoreFromSession = async (sess: LiteSession) => {
      console.log(LOG, '🔄 restoreFromSession() — storeId:', sess.storeId);
      try {
        const freshStore = await apiAdapter.getStore(
          sess.storeId,
          sess.merchantId,
          SoftDeleteFlag.EXCLUDE_DELETED,
        );
        console.log(
          LOG,
          '✅ getStore() returned — allow_limited_settings:',
          freshStore.allow_limited_settings,
        );
        if (!cancelled) {
          if (freshStore.allow_limited_settings) {
            console.log(
              LOG,
              '✅ Store has allow_limited_settings=true — restoring Recoil atoms',
            );
            setMerchant(sess.merchant as unknown as Merchant);
            setStore(freshStore);
            setAdmin(sess.employee as any);
            setActorFrom(sess.employee);
            setStoredToken(sess.token);

            // Recover the decrypted access token so authenticated API calls
            // (product sync, daily counts) work after a session restore.
            try {
              const decrypted = await apiAdapter.decryptUserData({
                encryptedCredentials: sess.token,
              });
              if (!cancelled) {
                setAccessToken(decrypted.AccessToken ?? '');
                ShopAndSmileAPIAdapter.setDefaultAccessToken(
                  decrypted.AccessToken || undefined,
                );
              }
            } catch (e) {
              console.warn(
                LOG,
                '⚠️ Could not recover access token on restore:',
                e,
              );
            }

            setIsValid(true);
            setErrorMessage(null);
          } else {
            console.log(
              LOG,
              '⚠️ Store allow_limited_settings=false — clearing session',
            );
            clearSession();
            setIsValid(false);
            setErrorMessage('Lite mode no longer enabled');
          }
        }
      } catch (e) {
        console.error(LOG, '❌ restoreFromSession() getStore failed:', e);
        if (!cancelled) {
          clearSession();
          setIsValid(false);
          setErrorMessage('Store or merchant no longer accessible');
        }
      }
    };

    const validate = async () => {
      console.log(LOG, '🔍 validate() started');

      // ── Check for stored session ──
      const sess = loadSession();
      if (sess) {
        console.log(LOG, '🔁 Session found in localStorage — restoring');
        await restoreFromSession(sess);
        return;
      }
      console.log(LOG, '📭 No session in localStorage — checking URL params');

      // ── Read URL params ──
      const token = getRawQueryParam('token');
      const merchantId = getRawQueryParam('merchant_id');
      const storeId = getRawQueryParam('store_id');

      console.log(
        LOG,
        '🔑 URL params — hasToken:',
        !!token,
        'merchantId:',
        merchantId,
        'storeId:',
        storeId,
      );

      if (!token || !merchantId || !storeId) {
        console.log(LOG, '❌ Missing required URL params — aborting');
        if (!cancelled) {
          setIsValid(false);
          setErrorMessage('Missing required URL parameters');
        }
        return;
      }

      // ── Decrypt ──
      try {
        console.log(LOG, '🔐 Calling decryptUserData...');
        const decrypted = await apiAdapter.decryptUserData({
          encryptedCredentials: decodeURIComponent(token),
        });
        console.log(
          LOG,
          '✅ decryptUserData succeeded — Employee.id:',
          decrypted.Employee?.id,
        );

        const employee = decrypted.Employee;
        if (!employee?.id) {
          console.log(LOG, '❌ No employee.id in decrypt response');
          if (!cancelled) {
            setIsValid(false);
            setErrorMessage('Invalid credentials');
          }
          return;
        }

        console.log(
          LOG,
          '🔍 Employee merchant_id:',
          employee.merchant_id,
          'vs URL merchantId:',
          merchantId,
        );
        if (employee.merchant_id !== merchantId) {
          console.log(LOG, '❌ Merchant mismatch');
          if (!cancelled) {
            setIsValid(false);
            setErrorMessage('User does not belong to this merchant');
          }
          return;
        }

        // ── Fetch store ──
        console.log(LOG, '🏪 Calling getStore() for storeId:', storeId);
        let store: Store;
        try {
          store = await apiAdapter.getStore(
            storeId,
            merchantId,
            SoftDeleteFlag.EXCLUDE_DELETED,
          );
          console.log(
            LOG,
            '✅ getStore() returned:',
            store.name,
            'allow_limited_settings:',
            store.allow_limited_settings,
          );
        } catch (e) {
          console.error(LOG, '❌ getStore() failed:', e);
          if (!cancelled) {
            setIsValid(false);
            setErrorMessage('Store not found');
          }
          return;
        }

        if (!store.allow_limited_settings) {
          console.log(LOG, '❌ Store allow_limited_settings is falsy');
          if (!cancelled) {
            setIsValid(false);
            setErrorMessage('Lite mode not enabled for this store');
          }
          return;
        }

        // ── Fetch merchant ──
        console.log(
          LOG,
          '🏢 Calling getMerchant() for merchantId:',
          merchantId,
        );
        let merchant: Merchant;
        try {
          merchant = await apiAdapter.getMerchant(merchantId);
          console.log(
            LOG,
            '✅ getMerchant() returned:',
            merchant.business_name,
          );
        } catch (e) {
          console.error(LOG, '❌ getMerchant() failed:', e);
          if (!cancelled) {
            setIsValid(false);
            setErrorMessage('Merchant not found');
          }
          return;
        }

        // ── Success ──
        console.log(
          LOG,
          '🎉 All checks passed — persisting session + Recoil atoms',
        );
        if (!cancelled) {
          saveSession({
            token,
            merchantId,
            storeId,
            employee: employee as unknown as Record<string, unknown>,
            merchant: merchant as unknown as Record<string, unknown>,
            store: store as unknown as Record<string, unknown>,
          });
          setStoredToken(token);
          setMerchant(merchant);
          setStore(store);
          setAdmin(employee);
          setActorFrom(employee);
          setAccessToken(decrypted.AccessToken ?? '');
          ShopAndSmileAPIAdapter.setDefaultAccessToken(
            decrypted.AccessToken || undefined,
          );
          setIsValid(true);
          setErrorMessage(null);
        }
      } catch (err) {
        console.error(LOG, '❌ validate() top-level catch:', err);
        if (!cancelled) {
          setIsValid(false);
          setErrorMessage(
            err instanceof Error ? err.message : 'Authentication failed',
          );
        }
      }
    };

    validate();
    return () => {
      console.log(LOG, '🧹 useEffect cleanup — cancelled=true');
      cancelled = true;
    };
  }, [setMerchant, setStore, setAdmin, setStoredToken, setAccessToken]);

  console.log(
    LOG,
    '🏁 Hook returning — isValid:',
    isValid,
    'errorMessage:',
    errorMessage,
  );
  return { isValid, errorMessage };
};
